import React, { useEffect, useMemo, useState } from 'react';
import './MiniLedger.css';

const MiniLedger = () => {
  const [rows, setRows] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const [newRow, setNewRow] = useState({
    date: '',
    libelle: '',
    debit: '',
    credit: ''
  });
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});

  const formatAmount = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toFixed(2);
  };

  const formatMonth = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

  const getMonthLabel = (date) =>
    date.toLocaleString('fr-FR', { month: 'long', year: 'numeric' });

  const sortRows = (inputRows) => {
    return [...inputRows].sort((a, b) => {
      const da = new Date(a.date_operation);
      const db = new Date(b.date_operation);
      if (da.getTime() !== db.getTime()) return da - db;
      const ordreA = Number(a.ordre ?? 0);
      const ordreB = Number(b.ordre ?? 0);
      if (ordreA !== ordreB) return ordreA - ordreB;
      return Number(a.id) - Number(b.id);
    });
  };

  const fetchRows = async (date) => {
    const month = formatMonth(date);
    const result = await window.api.getLedgerRows(month);

    const monthRows = Array.isArray(result?.rows) ? result.rows : [];
    const openingBalance = Number(result?.openingBalance) || 0;
    const orderedData = sortRows(monthRows);
    const excluded = orderedData
      .filter((row) => row.exclu === 1 || row.exclu === '1')
      .map((row) => row.id);

    const soldeRow = {
      id: 'init',
      date_operation: '',
      libelle: 'Solde initial',
      debit: '',
      credit: '',
      solde: openingBalance.toFixed(2)
    };

    setRows([soldeRow, ...orderedData]);
    setExcludedIds(excluded);
  };

  useEffect(() => {
    fetchRows(currentDate);
  }, []);

  const calculateSolde = () => {
    let solde = 0;
    return rows.map((row) => {
      if (row.id === 'init') {
        solde = parseFloat(row.solde) || 0;
      } else if (!excludedIds.includes(row.id)) {
        solde += (parseFloat(row.credit) || 0) - (parseFloat(row.debit) || 0);
      }
      return { ...row, solde: solde.toFixed(2) };
    });
  };

  const displayRows = useMemo(() => calculateSolde(), [rows, excludedIds]);

  const canMoveRow = (row, index, direction) => {
    if (row.id === 'init' || !row.date_operation) return false;

    let i = index + direction;
    while (i >= 0 && i < displayRows.length) {
      const candidate = displayRows[i];
      if (candidate.id === 'init') {
        i += direction;
        continue;
      }
      if (candidate.date_operation !== row.date_operation) return false;
      return true;
    }

    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewRow((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditRow((prev) => ({ ...prev, [name]: value }));
  };

  const changeMonth = (offset) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + offset);
    setCurrentDate(newDate);
    fetchRows(newDate);
  };

  const toggleExclusion = async (rowId, includeChecked) => {
    const exclu = includeChecked ? 0 : 1;
    const result = await window.api.toggleLedgerExclusion(rowId, exclu);
    if (result?.status === 'updated') {
      setExcludedIds((prev) =>
        exclu === 1 ? [...prev, rowId] : prev.filter((x) => x !== rowId)
      );
    }
  };

  const addRow = async () => {
    if (!newRow.date || !newRow.libelle.trim()) return;

    const result = await window.api.addLedgerRow({
      date: newRow.date,
      libelle: newRow.libelle.trim(),
      debit: newRow.debit || '0',
      credit: newRow.credit || '0'
    });

    if (result?.status === 'ok') {
      setNewRow({ date: '', libelle: '', debit: '', credit: '' });
      fetchRows(currentDate);
    }
  };

  const deleteRow = async (id) => {
    const result = await window.api.deleteLedgerRow(id);
    if (result?.status === 'deleted') {
      fetchRows(currentDate);
    }
  };

  const handleEditClick = (row) => {
    setEditingId(row.id);
    setEditRow({
      date: row.date_operation?.slice(0, 10) || '',
      libelle: row.libelle || '',
      debit: row.debit || '',
      credit: row.credit || ''
    });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditRow({});
  };

  const handleEditSave = async (id) => {
    const result = await window.api.updateLedgerRow({
      id,
      date: editRow.date,
      libelle: editRow.libelle,
      debit: editRow.debit || '0',
      credit: editRow.credit || '0'
    });

    if (result?.status === 'updated') {
      setEditingId(null);
      setEditRow({});
      fetchRows(currentDate);
    }
  };

  const moveRow = async (rowId, direction) => {
    const currentRows = [...rows];
    const index = currentRows.findIndex((r) => r.id === rowId);
    if (index === -1) return;

    const row = currentRows[index];
    if (row.id === 'init') return;

    const dateRef = row.date_operation;
    let j = index + direction;

    while (j >= 0 && j < currentRows.length) {
      const candidate = currentRows[j];
      if (candidate.id === 'init') {
        j += direction;
        continue;
      }
      if (candidate.date_operation !== dateRef) return;
      break;
    }

    if (j < 0 || j >= currentRows.length) return;

    const newRows = [...currentRows];
    [newRows[index], newRows[j]] = [newRows[j], newRows[index]];

    const sameDateRows = newRows.filter(
      (r) => r.id !== 'init' && r.date_operation === dateRef
    );

    const items = [];
    sameDateRows.forEach((r, idx) => {
      const globalIndex = newRows.findIndex((rr) => rr.id === r.id);
      newRows[globalIndex] = { ...newRows[globalIndex], ordre: idx + 1 };
      items.push({ id: r.id, ordre: idx + 1 });
    });

    setRows(newRows);
    await window.api.reorderLedgerRows(items);
  };

  return (
    <div>
      <h2>Feuille de calcul (filtrée par mois)</h2>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => changeMonth(-1)}>&larr;</button>
          <strong>{getMonthLabel(currentDate)}</strong>
          <button onClick={() => changeMonth(1)}>&rarr;</button>
        </div>

        <button onClick={() => window.print()}>🖨️ Imprimer</button>
      </div>

      <table border="2" cellPadding="5" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>Inclure</th>
            <th>Date</th>
            <th>Jour</th>
            <th>Libellé</th>
            <th>Débit</th>
            <th>Crédit</th>
            <th>Solde</th>
            <th>Ordre</th>
            <th>Modifier</th>
            <th>Supprimer</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, index) => (
            <tr key={row.id} className={row.id === 'init' ? 'init-row' : ''}>
              <td className="center">
                {row.id !== 'init' && (
                  <input
                    type="checkbox"
                    checked={!excludedIds.includes(row.id)}
                    //onChange={() => toggleExclusion(row.id, !excludedIds.includes(row.id))}
                    onChange={(e) => toggleExclusion(row.id, e.target.checked)}
                  />
                )}
              </td>

              {editingId === row.id ? (
                <>
                  <td><input type="date" name="date" value={editRow.date} onChange={handleEditChange} /></td>
                  <td>{editRow.date ? new Date(editRow.date).toLocaleDateString('fr-FR', { weekday: 'long' }).replace(/^\w/, (c) => c.toUpperCase()) : '-'}</td>
                  <td><input type="text" name="libelle" value={editRow.libelle} onChange={handleEditChange} /></td>
                  <td><input type="number" name="debit" value={editRow.debit} onChange={handleEditChange} /></td>
                  <td><input type="number" name="credit" value={editRow.credit} onChange={handleEditChange} /></td>
                  <td className="right">{row.solde}</td>
                  <td className="center"></td>
                  <td className="center">
                    <button onClick={() => handleEditSave(row.id)}>Enregistrer</button>
                    <button onClick={handleEditCancel}>Annuler</button>
                  </td>
                  <td className="center"></td>
                </>
              ) : (
                <>
                  <td>{row.date_operation ? new Date(row.date_operation).toLocaleDateString('fr-FR') : ''}</td>
                  <td>{row.date_operation ? new Date(row.date_operation).toLocaleDateString('fr-FR', { weekday: 'long' }).replace(/^\w/, (c) => c.toUpperCase()) : '-'}</td>
                  <td>{row.libelle}</td>
                  <td className="right">{formatAmount(row.debit)}</td>
                  <td className="right">{formatAmount(row.credit)}</td>
                  <td className="right">{formatAmount(row.solde)}</td>

                  <td className="center">
                    {row.id !== 'init' && row.date_operation && (
                      <div className="order-buttons">
                        <button
                          type="button"
                          title="Monter"
                          disabled={!canMoveRow(row, index, -1)}
                          onClick={() => canMoveRow(row, index, -1) && moveRow(row.id, -1)}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          title="Descendre"
                          disabled={!canMoveRow(row, index, 1)}
                          onClick={() => canMoveRow(row, index, 1) && moveRow(row.id, 1)}
                        >
                          ▼
                        </button>
                      </div>
                    )}
                  </td>

                  <td className="center">
                    {row.id !== 'init' && (
                      <button onClick={() => handleEditClick(row)}>Modifier</button>
                    )}
                  </td>
                  <td className="center">
                    {row.id !== 'init' && (
                      <button onClick={() => deleteRow(row.id)}>Supprimer</button>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}

          <tr>
            <td></td>
            <td><input type="date" name="date" value={newRow.date} onChange={handleChange} /></td>
            <td></td>
            <td><input type="text" name="libelle" value={newRow.libelle} onChange={handleChange} /></td>
            <td><input type="number" name="debit" value={newRow.debit}
                   onBlur={(e) =>
                                  setNewRow((prev) => ({
                                    ...prev,
                                    debit: formatAmount(e.target.value)
                                  })) }
                  onChange={handleChange} /></td>
            <td><input type="number" name="credit" value={newRow.credit}
             onBlur={(e) =>
                          setNewRow((prev) => ({
                            ...prev,
                            credit: formatAmount(e.target.value)
                          })) }
            onChange={handleChange} /></td>
            <td></td>
            <td></td>
            <td></td>
            <td className="center"><button onClick={addRow}>Ajouter</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default MiniLedger;
